import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import MainLayout from '@/components/layouts/MainLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Search, ArrowLeft, Home, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Helper component to display language-specific screenshots
interface ScreenshotProps {
  imageName: string;
  alt: string;
  className?: string;
  showDescription?: boolean;
}

interface ScreenshotInfo {
  id: string;
  name: string;
  alt: string;
}

interface Subsection {
  id: string;
  titleKey: string;
  contentKey: string;
  screenshots: ScreenshotInfo[];
}

interface Section {
  id: string;
  icon: string;
  titleKey: string;
  contentKey: string;
  screenshots: ScreenshotInfo[];
  subsections: Subsection[];
}

const Screenshot = ({ 
  imageName, 
  alt, 
  className = "mt-4 mb-6 rounded-md shadow-md border border-gray-200", 
  showDescription = true 
}: ScreenshotProps) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language === 'el' ? 'el' : 'en';
  
  // Handle different image formats and naming conventions
  let imagePath: string;
  if (imageName.startsWith('PWA-')) {
    // Use exact filename from the directory
    imagePath = `/assets/manual_${language}/${imageName}-${language}.jpg`;
  } else {
    // Use standard naming for PNG files
    imagePath = `/assets/manual_${language}/${imageName}_${language}.png`;
  }
  
  const title = t(`common.help.screenshots.${imageName}.title`, '');
  const description = t(`common.help.screenshots.${imageName}.description`, '');
  
  return (
    <div className="mb-8">
      <div className="relative">
        <img 
          src={imagePath} 
          alt={alt} 
          className={className}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        {showDescription && title && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="absolute top-2 right-2 rounded-full p-1 bg-white/80 backdrop-blur-sm"
                >
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-md p-4">
                <h4 className="font-medium mb-2">{title}</h4>
                <p className="text-sm">{description}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      
      {showDescription && title && (
        <div className="mt-2">
          <h4 className="font-medium text-sm">{title}</h4>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      )}
    </div>
  );
};

const HelpPage = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSection, setCurrentSection] = useState('dashboard');

  const sections = [
    {
      id: 'dashboard',
      icon: '📊',
      titleKey: 'common.help.dashboard.title',
      contentKey: 'common.help.dashboard.content',
      screenshots: [
        { id: 'dashboard_system_overview', name: 'dashboard_system_overview', alt: 'System Overview Dashboard' },
        { id: 'dashboard_devices', name: 'dashboard_devices', alt: 'Devices Overview' },
      ],
      subsections: [
        {
          id: 'dashboard-summary',
          titleKey: 'common.help.dashboard.summary.title',
          contentKey: 'common.help.dashboard.summary.content',
          screenshots: [{ id: 'dashboard_summary_1', name: 'dashboard_system_overview', alt: 'Dashboard Summary' }],
        },
        {
          id: 'dashboard-devices',
          titleKey: 'common.help.dashboard.devices.title',
          contentKey: 'common.help.dashboard.devices.content',
          screenshots: [{ id: 'dashboard_devices_1', name: 'dashboard_devices', alt: 'Dashboard Devices' }],
        }
      ]
    },
    {
      id: 'devices',
      icon: '🔌',
      titleKey: 'common.help.devices.title',
      contentKey: 'common.help.devices.content',
      screenshots: [],
      subsections: [
        {
          id: 'devices-management',
          titleKey: 'common.help.devices.management.title',
          contentKey: 'common.help.devices.management.content',
          screenshots: [{ id: 'devices_management_1', name: 'devices_management', alt: 'Device Management' }],
        },
        {
          id: 'devices-map',
          titleKey: 'common.help.devices.map.title',
          contentKey: 'common.help.devices.map.content',
          screenshots: [
            { id: 'devices_map_1', name: 'devices_map', alt: 'Device Map' },
            { id: 'devices_map_2', name: 'devices_map_active_device', alt: 'Device Map with Active Device' },
          ],
        },
        {
          id: 'devices-schedules',
          titleKey: 'common.help.devices.schedules.title',
          contentKey: 'common.help.devices.schedules.content',
          screenshots: [
            { id: 'schedules_1', name: 'schedules_devices', alt: 'Device Schedules' },
            { id: 'bulk_update_1', name: 'bulk_update', alt: 'Bulk Update Interface' },
            { id: 'device_control_1', name: 'device_control', alt: 'Device Control' },
          ],
        },
        {
          id: 'devices-charts',
          titleKey: 'common.help.devices.charts.title',
          contentKey: 'common.help.devices.charts.content',
          screenshots: [
            { id: 'energy_charts_1_id', name: 'energy_charts_1', alt: 'Energy Consumption Overview' },
            { id: 'energy_charts_2_id', name: 'energy_charts_2', alt: 'Device Comparison Chart' },
            // { id: 'energy_charts_3_id', name: 'energy_charts_3', alt: 'Time-of-Use Analysis' },
            // { id: 'energy_charts_4_id', name: 'energy_charts_4', alt: 'Cost Analysis Dashboard' },
            // { id: 'energy_charts_5_id', name: 'energy_charts_5', alt: 'Efficiency Recommendations' },
          ],
        }
      ]
    },
    {
      id: 'system_events',
      icon: '📋',
      titleKey: 'common.help.system_events.title',
      contentKey: 'common.help.system_events.content',
      screenshots: [
        { id: 'events_overview', name: 'events', alt: 'System Events Overview' },
      ],
      subsections: [
        {
          id: 'events-filtering',
          titleKey: 'common.help.system_events.filtering.title',
          contentKey: 'common.help.system_events.filtering.content',
          screenshots: [{ id: 'events_filtering', name: 'events', alt: 'Event Filtering' }],
        },
        {
          id: 'events-types',
          titleKey: 'common.help.system_events.types.title',
          contentKey: 'common.help.system_events.types.content',
          screenshots: [],
        }
      ]
    },
    {
      id: 'account',
      icon: '👤',
      titleKey: 'common.help.account.title',
      contentKey: 'common.help.account.content',
      screenshots: [],
      subsections: [
        {
          id: 'account-profile',
          titleKey: 'common.help.account.profile.title',
          contentKey: 'common.help.account.profile.content',
          screenshots: [{ id: 'account_profile_1', name: 'account', alt: 'Account Profile' }],
        },
        {
          id: 'account-security',
          titleKey: 'common.help.account.security.title',
          contentKey: 'common.help.account.security.content',
          screenshots: [{ id: 'security_settings_1', name: 'security', alt: 'Security Settings' }],
        }
      ]
    },
    {
      id: 'pwa',
      icon: '📱',
      titleKey: 'common.help.pwa.title',
      contentKey: 'common.help.pwa.content',
      screenshots: [],
      subsections: [
        {
          id: 'pwa-install',
          titleKey: 'common.help.pwa.install.title',
          contentKey: 'common.help.pwa.install.content',
          screenshots: [
            // Using empty array here as we'll render these manually
          ]
        },
        {
          id: 'pwa-benefits',
          titleKey: 'common.help.pwa.benefits.title',
          contentKey: 'common.help.pwa.benefits.content',
          screenshots: [],
        }
      ]
    }
  ];

  // Find sections matching search term
  const filteredSections = searchTerm 
    ? sections.filter(section => 
        t(section.titleKey).toLowerCase().includes(searchTerm.toLowerCase()) ||
        section.subsections.some(sub => 
          t(sub.titleKey).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : sections;

  // Handle navigation between sections
  const navigateSection = (direction: 'prev' | 'next') => {
    const currentIndex = sections.findIndex(s => s.id === currentSection);
    if (direction === 'prev' && currentIndex > 0) {
      setCurrentSection(sections[currentIndex - 1].id);
    } else if (direction === 'next' && currentIndex < sections.length - 1) {
      setCurrentSection(sections[currentIndex + 1].id);
    }
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-6">
        {/* Back to App Button */}
        <div className="mb-4">
          <Link to="/dashboard">
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span>{t('common.backToApp')}</span>
            </Button>
          </Link>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>{t('common.help.title')}</CardTitle>
            <CardDescription>
              {t('common.help.subtitle')}
            </CardDescription>
            
            <div className="flex items-center space-x-2 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('common.help.search')}
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Tabs 
                defaultValue={i18n.language} 
                className="w-[150px]"
                onValueChange={(value) => i18n.changeLanguage(value)}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="en">EN</TabsTrigger>
                  <TabsTrigger value="el">EL</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Sidebar Navigation */}
              <div className="md:col-span-1 space-y-4">
                <div className="font-medium text-sm text-muted-foreground mb-2">
                  {t('common.help.topics')}
                </div>
                
                {/* Home Button */}
                <Link to="/dashboard" className="block mb-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    {t('common.returnToDashboard')}
                  </Button>
                </Link>
                
                <nav className="space-y-1">
                  {filteredSections.map(section => (
                    <Button
                      key={section.id}
                      variant={currentSection === section.id ? "default" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setCurrentSection(section.id)}
                    >
                      <span className="mr-2">{section.icon}</span>
                      {t(section.titleKey)}
                    </Button>
                  ))}
                </nav>
              </div>
              
              {/* Main Content */}
              <div className="md:col-span-3">
                {filteredSections.map(section => (
                  section.id === currentSection && (
                    <div key={section.id} className="space-y-6">
                      <div>
                        <h2 className="text-2xl font-bold mb-2">
                          {section.icon} {t(section.titleKey)}
                        </h2>
                        <p className="text-gray-600">
                          {t(section.contentKey)}
                        </p>
                        
                        {/* Section main screenshots */}
                        {section.screenshots && section.screenshots.length > 0 && (
                          <div className="mt-4">
                            {section.screenshots.map((screenshot) => (
                              <Screenshot 
                                key={screenshot.id}
                                imageName={screenshot.name}
                                alt={screenshot.alt}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <Accordion type="single" collapsible className="w-full">
                        {section.subsections.map(subsection => (
                          <AccordionItem key={subsection.id} value={subsection.id}>
                            <AccordionTrigger>
                              {t(subsection.titleKey)}
                            </AccordionTrigger>
                            <AccordionContent>
                              <p>{t(subsection.contentKey)}</p>
                              
                              {/* Custom rendering for PWA install section */}
                              {section.id === 'pwa' && subsection.id === 'pwa-install' && (
                                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                                  <div className="flex-1 max-w-[250px]">
                                    <Screenshot 
                                      imageName="PWA-Add-to-Homescreen" 
                                      alt="Adding PWA to home screen" 
                                      className="w-full h-auto rounded-md shadow-md border border-gray-200"
                                      showDescription={false}
                                    />
                                    <p className="text-sm text-center mt-2">{t('common.help.pwa.addToHomescreen')}</p>
                                  </div>
                                  <div className="flex-1 max-w-[250px]">
                                    <Screenshot 
                                      imageName="PWA-install" 
                                      alt="Installing the PWA" 
                                      className="w-full h-auto rounded-md shadow-md border border-gray-200"
                                      showDescription={false}
                                    />
                                    <p className="text-sm text-center mt-2">{t('common.help.pwa.installApp')}</p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Subsection screenshots */}
                              {subsection.screenshots && subsection.screenshots.length > 0 && (
                                <div className="mt-4 space-y-4">
                                  {subsection.screenshots.map((screenshot) => (
                                    <Screenshot 
                                      key={screenshot.id}
                                      imageName={screenshot.name}
                                      alt={`${t(subsection.titleKey)} screenshot`}
                                    />
                                  ))}
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                      
                      {/* Navigation Buttons */}
                      <div className="flex justify-between pt-4 mt-6 border-t">
                        <Button
                          variant="outline"
                          onClick={() => navigateSection('prev')}
                          disabled={sections.findIndex(s => s.id === currentSection) === 0}
                        >
                          <ChevronLeft className="h-4 w-4 mr-2" />
                          {t('common.help.previous')}
                        </Button>
                        
                        <Button
                          variant="outline"
                          onClick={() => navigateSection('next')}
                          disabled={sections.findIndex(s => s.id === currentSection) === sections.length - 1}
                        >
                          {t('common.help.next')}
                          <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  )
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default HelpPage; 